import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { catalog } from '../api/index.js';
import { Badge, statusBadge } from '../components/ui/badge.js';
import { Card } from '../components/ui/card.js';
import { Loader2, Search, AlertCircle, FolderOpen } from 'lucide-react';
import { SearchableSelect } from '../components/SearchableSelect.js';
import { format } from 'date-fns';

function fmtDate(v: unknown): string {
  if (!v || typeof v !== 'string') return '—';
  try { return format(new Date(v), 'MMM d, yyyy'); } catch { return v; }
}

export function GroupList() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const preselectedSeries = searchParams.get('series') ?? '';
  const [selectedSeries, setSelectedSeries] = useState(preselectedSeries);
  const [search, setSearch] = useState('');

  const { data: seriesData } = useQuery({
    queryKey: ['catalog-series'],
    queryFn: catalog.series,
    staleTime: 10 * 60 * 1000,
  });

  // Fetch all groups (or by series if selected)
  const { data: groupsData, isLoading, error } = useQuery({
    queryKey: ['catalog-groups', selectedSeries],
    queryFn: () => catalog.groups(selectedSeries || undefined),
    staleTime: 5 * 60 * 1000,
  });

  const seriesList = seriesData?.series ?? [];
  const seriesPickOptions = useMemo(
    () => [{ value: '', label: 'All series' }, ...seriesList.map((s) => ({ value: s._id, label: s.name }))],
    [seriesList],
  );
  const seriesNameById = useMemo(
    () => new Map(seriesList.map((s) => [s._id, s.name])),
    [seriesList],
  );

  const allGroups = groupsData?.groups ?? [];
  const filtered = search
    ? allGroups.filter(g => g.name.toLowerCase().includes(search.toLowerCase()))
    : allGroups;

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FolderOpen size={22} className="text-brand-600" /> Groupings
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          {allGroups.length} grouping{allGroups.length !== 1 ? 's' : ''}{selectedSeries ? ' in series' : ' across all series'}
        </p>
      </div>

      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="min-w-[14rem]">
          <SearchableSelect
            value={selectedSeries}
            onChange={(v) => {
              setSelectedSeries(v);
              setSearch('');
            }}
            options={seriesPickOptions}
            placeholder="All series"
            allowClear={false}
          />
        </div>

        <div className="relative flex-1 min-w-48">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search groupings…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>
      </div>

      <Card>
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <Loader2 className="animate-spin mr-2" /> Loading…
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-16 text-red-500 gap-2 text-sm">
            <AlertCircle size={16} /> Failed to load groupings
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Group Name</th>
                  {!selectedSeries && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Series</th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Release</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Slug</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map(g => (
                  <tr key={g._id} onClick={() => navigate(`/groups/${g._id}`)} className="hover:bg-gray-50 cursor-pointer transition-colors">
                    <td className="px-4 py-3 font-medium text-gray-900">{g.name}</td>
                    {!selectedSeries && (
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {g.seriesName || (g.seriesId ? (seriesNameById.get(g.seriesId) ?? g.seriesId) : '—')}
                      </td>
                    )}
                    <td className="px-4 py-3">
                      {(g as any).status ? <Badge variant={statusBadge((g as any).status)}>{(g as any).status}</Badge> : '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{fmtDate((g as any).releaseAt)}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs font-mono">{(g as any).slug ?? '—'}</td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={selectedSeries ? 4 : 5} className="px-5 py-12 text-center text-gray-400">
                      {search ? 'No groupings match the search' : 'No groupings found'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
